import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { EASE_OUT } from '@/lib/motion';
import { CourseDetail, CourseModule, CourseSectionWithModules } from '../../types/course';
import ModulePreview from './ModulePreview';
import ModuleViewContainer from '../modules/views/ModuleViewContainer';
import { getEvalViewEntry, getFeedbackViewEntry } from '../modules/views/registry';
import { BackPill } from '../programs/preview/PreviewWelcomeHero';
import StickyProgressRow from '../learner/StickyProgressRow';

interface FlatModule {
  module: CourseModule;
  section: CourseSectionWithModules;
}

interface CoursePreviewPlayerProps {
  course: CourseDetail;
  /** Leave the immersive preview (back to wherever the author came from). */
  onExit: () => void;
  /** Drive module navigation via the `?m=` URL param (default). Set false to
   *  navigate via internal state instead — used inside modals/overlays and the
   *  full preview experience where writing to the page URL is undesirable. */
  urlSync?: boolean;
  /** Module to open on first render (state-nav only). Lets the surrounding
   *  experience drop the learner into a specific module from a section list. */
  startModuleId?: string;
  /** When provided, the header shows a "Back" pill that returns to the current
   *  module's section (mirrors the learner's "Go back to modules"). */
  onBackToSection?: (sectionId: string) => void;
}

const END = 'end';

// Admin evaluation/feedback items carry their full (unsanitized) config; the
// registry Views only read `config.question` / `config.options`, so passing the
// admin item straight through is safe and shows no answer keys.
interface PreviewItem {
  id: string;
  type: string;
  order_index?: number;
}

function flatten(course: CourseDetail): FlatModule[] {
  const out: FlatModule[] = [];
  (course.sections ?? [])
    .slice()
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .forEach((section) => {
      (section.modules ?? [])
        .slice()
        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        .forEach((module) => out.push({ module, section }));
    });
  return out;
}

function sortedItems(module: CourseModule): PreviewItem[] {
  const items = (module.details?.items as PreviewItem[] | undefined) ?? [];
  return items.slice().sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
}

/**
 * Immersive course preview that mirrors the learner course-taking flow: it
 * flattens the admin course into one ordered module sequence and steps through
 * it a single module at a time, rendering each via the canonical
 * `components/modules/views` (content through the shared ModulePlayer;
 * evaluation/feedback inline in disabled preview mode). No progress is written —
 * it's a free-roam author preview, so prev/next navigate without gating.
 */
const CoursePreviewPlayer: React.FC<CoursePreviewPlayerProps> = ({
  course,
  onExit,
  urlSync = true,
  startModuleId,
  onBackToSection,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localModule, setLocalModule] = useState<string | null>(startModuleId ?? null);
  const reduceMotion = useReducedMotion();

  const flat = useMemo(() => flatten(course), [course]);

  const moduleParam = urlSync ? searchParams.get('m') : localModule;
  const atEnd = moduleParam === END && flat.length > 0;
  const foundIdx = flat.findIndex((f) => f.module.id === moduleParam);
  const currentIdx = atEnd ? flat.length : foundIdx >= 0 ? foundIdx : 0;
  const current = atEnd ? null : flat[currentIdx] ?? null;

  const goTo = (id: string) => {
    if (!urlSync) {
      setLocalModule(id);
      return;
    }
    const params = new URLSearchParams(searchParams);
    params.set('m', id);
    // Replace (not push) so per-module steps don't pile up in history — the
    // admin "Back" / browser-back then exits the preview, not step-by-step.
    setSearchParams(params, { replace: true });
  };

  const goNext = () => {
    if (currentIdx < flat.length - 1) goTo(flat[currentIdx + 1].module.id);
    else goTo(END);
  };
  const goPrev = () => {
    if (atEnd) goTo(flat[flat.length - 1].module.id);
    else if (currentIdx > 0) goTo(flat[currentIdx - 1].module.id);
  };

  if (flat.length === 0) {
    return (
      <div className="w-[90%] sm:w-[80%] mx-auto py-16 text-center" data-testid="preview-empty">
        <p className="text-sm text-text-secondary max-w-[420px] mx-auto">
          This course doesn&apos;t have any content yet. Add a section and at least one module to
          preview it as a learner.
        </p>
      </div>
    );
  }

  if (atEnd) {
    return (
      <PreviewEndPanel
        total={flat.length}
        onRestart={() => goTo(flat[0].module.id)}
        onExit={onExit}
      />
    );
  }

  if (!current) return null;

  const isLast = currentIdx === flat.length - 1;
  const percent = ((currentIdx + 1) / flat.length) * 100;

  const enter = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
      };

  return (
    <div className="w-[95%] sm:w-[90%] mx-auto py-6 flex-1 flex flex-col gap-6">
      <PreviewHeader
        sectionTitle={current.section.title || 'Section'}
        position={currentIdx + 1}
        total={flat.length}
        percent={percent}
        canPrev={currentIdx > 0}
        onPrev={goPrev}
        onNext={goNext}
        nextLabel={isLast ? 'Finish preview' : 'Next'}
        nextFinish={isLast}
        onBack={onBackToSection ? () => onBackToSection(current.section.id) : undefined}
      />

      {/* Natural height (no min-h-0 clamp): long content grows the page and the
          surrounding shell (overlay body / CoursePlayerLayout main) scrolls, so
          nothing is visually capped behind a nested scrollbar. */}
      <div className="flex-1 flex flex-col pb-2">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={current.module.id}
            className="flex-1 flex flex-col"
            initial={enter.initial}
            animate={enter.animate}
            exit={enter.exit}
            transition={{ duration: 0.15, ease: EASE_OUT }}
          >
            <ModuleBody module={current.module} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─── Module body dispatch ────────────────────────────────────────────────────

const ModuleBody: React.FC<{ module: CourseModule }> = ({ module }) => {
  if (module.type === 'evaluation') return <EvaluationPreview module={module} />;
  if (module.type === 'feedback') return <FeedbackPreview module={module} />;
  // Content modules render through the natural-height preview views
  // (mode='preview'), NOT the learner runtime: the learner views size
  // themselves to a fixed viewport chain and trap long rich text behind an
  // internal scrollbox (looks "capped" and can clip embedded images). The
  // preview views flow at full content height — the page scrolls instead —
  // and RichTextBody re-signs `<img data-upload-key>` so images always load.
  return (
    <div className="rounded-2xl bg-surface-primary border border-border-secondary shadow-[0_4px_10px_rgba(0,0,0,0.06)] p-4 sm:p-6">
      <ModulePreview module={module} />
    </div>
  );
};

const QuestionCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-2xl bg-surface-primary border border-border-secondary shadow-[0_4px_10px_rgba(0,0,0,0.06)] p-4 sm:p-5">
    {children}
  </div>
);

const EvaluationPreview: React.FC<{ module: CourseModule }> = ({ module }) => {
  const items = sortedItems(module);
  const passing = module.details?.passing_grade_percent as number | undefined;

  return (
    <ModuleViewContainer>
      <div className="flex flex-col gap-5">
        {module.title && (
          <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
            {module.title}
          </h2>
        )}
        {typeof passing === 'number' && (
          <p className="text-sm text-text-secondary">
            Passing grade: <span className="font-semibold text-text-primary">{passing}%</span>
          </p>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-text-secondary italic">No questions yet.</p>
        ) : (
          items.map((it) => {
            const { View } = getEvalViewEntry(it.type as Parameters<typeof getEvalViewEntry>[0]);
            return (
              <QuestionCard key={it.id}>
                <View item={it} mode="preview" disabled />
              </QuestionCard>
            );
          })
        )}
      </div>
    </ModuleViewContainer>
  );
};

const FeedbackPreview: React.FC<{ module: CourseModule }> = ({ module }) => {
  const items = sortedItems(module);
  const anonymous = Boolean(module.details?.anonymous);

  return (
    <ModuleViewContainer>
      <div className="flex flex-col gap-5">
        {module.title && (
          <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
            {module.title}
          </h2>
        )}
        {anonymous && (
          <p className="text-sm text-text-secondary italic">Responses collected anonymously.</p>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-text-secondary italic">No questions yet.</p>
        ) : (
          items.map((it) => {
            const { View } = getFeedbackViewEntry(
              it.type as Parameters<typeof getFeedbackViewEntry>[0]
            );
            return (
              <QuestionCard key={it.id}>
                <View item={it} mode="preview" disabled />
              </QuestionCard>
            );
          })
        )}
      </div>
    </ModuleViewContainer>
  );
};

// ─── Header ──────────────────────────────────────────────────────────────────

interface PreviewHeaderProps {
  sectionTitle: string;
  position: number;
  total: number;
  percent: number;
  canPrev: boolean;
  onPrev: () => void;
  onNext: () => void;
  nextLabel: string;
  nextFinish: boolean;
  /** When set, a "Back" pill returns to the section's module list. */
  onBack?: () => void;
}

const PreviewHeader: React.FC<PreviewHeaderProps> = ({
  sectionTitle,
  position,
  total,
  percent,
  canPrev,
  onPrev,
  onNext,
  nextLabel,
  nextFinish,
  onBack,
}) => {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && <BackPill label="Back" onClick={onBack} />}
          <h1 className="text-xl sm:text-2xl font-extrabold text-text-primary leading-tight truncate">
            {sectionTitle}
          </h1>
        </div>
        <span className="shrink-0 text-xs font-semibold text-text-secondary tabular-nums">
          Module {position} of {total}
        </span>
      </div>

      {/* Pinned while the previewed module scrolls — same behaviour the
          learner gets from StickyProgressRow in LearnerModuleHeader. */}
      <StickyProgressRow>
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 sm:gap-x-12">
        <button
          type="button"
          onClick={onPrev}
          disabled={!canPrev}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-secondary text-secondary text-sm font-bold hover:bg-secondary/10 active:scale-[0.98] transition-[background-color,transform] duration-150 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Previous
        </button>

        <div
          className="relative flex-1 min-w-[180px] basis-full sm:basis-0 h-[58px] flex items-center pr-[29px]"
          role="progressbar"
          aria-valuenow={clamped}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="relative w-full h-2.5 rounded-full bg-neutral-200/70 dark:bg-white/25 shadow-[0_0_19.8px_rgba(0,0,0,0.12)]">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-primary-500 transition-[width] duration-500 ease-out"
              style={{ width: `${clamped}%` }}
            />
            <div className="pointer-events-none absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 size-[58px] flex items-center justify-center rounded-full bg-neutral-200/80 dark:bg-white/20 drop-shadow-[0_0_9.9px_rgba(0,0,0,0.12)] text-text-primary dark:text-white text-[13px] font-semibold">
              {clamped}%
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onNext}
          className={`inline-flex items-center justify-center gap-2 min-w-[151px] h-9 px-4 rounded-full text-sm font-bold shrink-0 active:scale-[0.97] transition-[background-color,transform] duration-150 hover:brightness-110 shadow-[0_2px_8px_rgba(105,72,170,0.25)] ${ nextFinish ? 'bg-gradient-to-t from-success-600 to-success-500' : 'bg-gradient-to-t from-primary-700 to-primary-500' }`}
        >
          {nextFinish ? <CheckCircleIcon className="w-4 h-4" /> : null}
          {nextLabel}
          {!nextFinish ? <ArrowRightIcon className="w-4 h-4" /> : null}
        </button>
      </div>
      </StickyProgressRow>
    </>
  );
};

// ─── End panel ───────────────────────────────────────────────────────────────

const PreviewEndPanel: React.FC<{ total: number; onRestart: () => void; onExit: () => void }> = ({
  total,
  onRestart,
  onExit,
}) => (
  <div
    className="w-[90%] sm:w-[80%] mx-auto py-20 flex flex-col items-center text-center"
    data-testid="preview-end"
  >
    <div className="w-16 h-16 rounded-full bg-success-50 dark:bg-success-900/20 flex items-center justify-center mb-5">
      <CheckCircleIcon className="w-9 h-9 text-success-500" />
    </div>
    <h2 className="text-xl font-bold text-text-primary mb-2">End of Preview</h2>
    <p className="text-sm text-text-secondary mb-6 max-w-[420px]">
      That&apos;s everything a learner sees across {total} {total === 1 ? 'module' : 'modules'}.
    </p>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onRestart}
        className="px-5 py-2.5 text-sm font-bold text-text-primary border border-border-primary/40 rounded-full hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
      >
        Restart preview
      </button>
      <button
        type="button"
        onClick={onExit}
        className="px-5 py-2.5 text-sm font-bold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-150"
      >
        Exit preview
      </button>
    </div>
  </div>
);

export default CoursePreviewPlayer;
